/**
 * Crews, as they appear around the app.
 *
 * Three pieces: the tag chip that rides beside a name, the panel that opens
 * when somebody clicks it, and the pieces the crew screen shares with it.
 *
 * The chip is the whole feature in miniature - it is the only part most
 * people ever touch, it appears in every list in the app, and it has to cost
 * almost nothing to draw. So it renders from the stub the server already
 * attaches to every user, and only fetches the crew itself once somebody
 * actually opens one.
 *
 * The panel is a profile for a group: a banner, a mark, a level and a bar
 * towards the next one, then the people. It used to be a 440px column of
 * labelled rows, which is a settings screen wearing a crew's name - fine for
 * changing something, useless for looking at one.
 */

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { go } from '@/lib/hash';
import { useSession } from '@/stores/session';
import type { PublicUser } from '@/lib/types';
import { cn } from '@/lib/cn';
import { useBackdropClose } from '@/lib/backdrop';
import { Icon } from '@/icons/Icon';
import { Avatar } from './Avatar';
import { UserName, openUserCard, closeUserCard } from './user';
import { closeCrewHover } from './CrewTag';
import { Button, useConfirm, useToast } from './ui';
import {
  CrewBanner, CrewLevelBar, CrewMark, crewColor,
  type CrewAppearance, type CrewLevel
} from './CrewArt';
import { CrewWorkspace } from './CrewWorkspace';
import { CrewPreview } from './CrewPreview';

/*
 * The tag itself lives in CrewTag.tsx - twenty themes, the emblems, the
 * shapes and the card that opens on hover. Re-exported from here because
 * three dozen files already import CrewTag from this module and the tag
 * being a bigger thing than it was is not their business.
 */
export { CrewTag, wireCrewOpener } from './CrewTag';
import type { CrewChip } from './CrewTag';
export type { CrewChip, CrewDesign, CrewTagFont, CrewTagSize } from './CrewTag';
import { wireCrewOpener as wireOpener } from './CrewTag';

export type CrewMember = { role: 'owner' | 'officer' | 'member'; joinedAt: number; user: PublicUser };

export type CrewEvent = {
  id: string;
  title: string;
  description: string;
  gameId: string | null;
  startsAt: number;
  /** When it is over; the server stops listing it from then. */
  endsAt: number;
  durationMin: number;
  /** Started and not yet over. */
  live: boolean;
  createdAt: number;
  /** A series: the next one is made as this one ends. */
  repeat?: 'weekly' | 'fortnightly' | null;
  createdBy: PublicUser | null;
  goingCount: number;
  maybeCount?: number;
  cantCount?: number;
  going: PublicUser[];
  maybe?: PublicUser[];
  imGoing: boolean;
  /** What you answered: going, maybe, cant, or nothing yet. */
  myAnswer?: 'going' | 'maybe' | 'cant' | null;
};

export type Crew = {
  id: string;
  tag: string;
  name: string;
  description: string;
  language: string;
  region: string;
  tags: string[];
  events: CrewEvent[];
  /** The person looking has an invite to this crew. */
  invited: boolean;
  /** The crew's standing voice room, for the voice store. */
  voiceRoom: string;
  createdAt: number;
  memberCount: number;
  settings: {
    join: 'open' | 'request' | 'invite';
    viewMembers: 'everyone' | 'members';
    /** How findable it is, separate from how somebody gets in. */
    visibility?: 'public' | 'unlisted' | 'private';
  };
  /** One line under the name, wherever the crew is drawn small. */
  tagline?: string;
  /** What a new member is told, and whether they are told it. */
  welcome?: string;
  welcomeOn?: boolean;
  /** What the person looking may actually do here. */
  myPermissions?: string[];
  myRole: 'owner' | 'officer' | 'member' | null;
  owner: PublicUser | null;
  score: { xp: number; levels: number; members: number };
  level: CrewLevel;
  appearance: CrewAppearance;
  /** Which tag looks this crew has earned. The picker locks the rest. */
  unlockedTags?: string[];
  /** The tag as it is drawn beside a name. */
  chip?: CrewChip | null;
  /** The crew's roles, in rank order. Absent on an older payload. */
  roles?: import('./CrewRoles').CrewRole[];
  /** When the tag may next change, or null when it may change now. */
  canRetagAt: number | null;
  membersHidden: boolean;
  members: CrewMember[];
  requests: { at: number; user: PublicUser }[];
  rank?: number;
};

/* ------------------------------------------------------------- the chip */

/**
 * Which crew panel is open, as one value for the whole app.
 *
 * The chip appears inside chat rows, rosters and profile headers - all of
 * which are already inside something clickable. Rather than every one of them
 * carrying a panel, they all set this and one host draws it.
 */
let openTag: string | null = null;
const watchers = new Set<() => void>();

export function openCrew(tag: string): void {
  /* Whatever smaller thing was open under this is gone: a profile card left
     behind a crew preview is a card nobody can reach to close. */
  closeUserCard();
  closeCrewHover();
  openTag = tag;
  watchers.forEach((fn) => fn());
}

/** Shuts the crew preview, from anywhere. */
export function closeCrewPreview(): void {
  if (!openTag) return;
  openTag = null;
  watchers.forEach((fn) => fn());
}

/* So a click on a tag anywhere opens this panel. */
wireOpener(openCrew);

function closeCrew(): void {
  openTag = null;
  watchers.forEach((fn) => fn());
}

/* ------------------------------------------------------------ the panel */

/** Draws whichever crew was last clicked. Mounted once, near the root. */
export function CrewHost() {
  const [, tick] = useState(0);
  useEffect(() => {
    const fn = () => tick((n) => n + 1);
    watchers.add(fn);
    return () => { watchers.delete(fn); };
  }, []);

  if (!openTag) return null;
  /* A preview, not the whole screen in a box. Somebody who pressed four
     characters in a chat line wanted to know who these people are, not to be
     moved into a second application - and 'Open crew' is right there when
     they do want that. */
  return (
    <CrewPreview
      tag={openTag}
      onClose={closeCrew}
      onOpenFull={() => { closeCrew(); go('crews'); }}
    />
  );
}

export function CrewPanel({ tag, onClose }: { tag: string; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['crew', tag],
    queryFn: () => api.get<{ crew: Crew }>('/api/crews/' + encodeURIComponent(tag))
  });
  const crew = q.data?.crew ?? null;

  const backdrop = useBackdropClose(onClose);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      data-keeps-card
      className="soft-in fixed inset-0 z-[300] grid place-items-center bg-[rgba(3,5,8,.62)] p-4 backdrop-blur-[4px]"
      {...backdrop}
    >
      <div role="dialog" aria-modal="true" aria-label={crew ? crew.name : 'Crew'} className="pugo-panel profile-in flex h-[min(760px,88vh)] w-full max-w-[1040px] flex-col overflow-hidden rounded-[calc(24px*var(--radius-mul,1))] border border-line bg-surface-0 shadow-[0_40px_90px_-20px_rgba(0,0,0,.7)]">
        {!crew ? (
          <p className="p-10 text-center text-[13px] text-mute">
            {q.isError ? 'No crew with that tag.' : 'Opening…'}
          </p>
        ) : (
          <CrewBody crew={crew} tag={tag} onClose={onClose} />
        )}
      </div>
    </div>
  );
}

/**
 * The crew itself.
 *
 * Split out from the panel so the crew screen can draw exactly the same thing
 * full width, with no dialog around it - which is the whole point of having a
 * screen for your own crew rather than a second, smaller design for it.
 */
export function CrewBody({ crew, tag, onClose }: {
  crew: Crew;
  tag: string;
  /** Present when this is inside the panel. The screen has nowhere to close to. */
  onClose?: () => void;
}) {
  const me = useSession((s) => s.me);
  const queryClient = useQueryClient();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reason, setReason] = useState('');

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['crew', tag] });
    void queryClient.invalidateQueries({ queryKey: ['crews'] });
    void queryClient.invalidateQueries({ queryKey: ['crew-mine'] });
    void useSession.getState().refresh();
  };

  async function act(path: string, body?: unknown, note?: string) {
    setBusy(true);
    try {
      await api.post(path, body ?? {});
      refresh();
      if (note) toast(note, 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  const mine = crew.myRole;
  const canManage = mine === 'owner' || mine === 'officer';
  const colour = crewColor(crew.appearance);
  const joinLabel = crew.settings.join === 'open' ? 'Join crew' : crew.settings.join === 'request' ? 'Ask to join' : 'Invite only';
  /* Leaders first, then whoever is around, then everyone else. */
  const roleRank = { owner: 0, officer: 1, member: 2 } as const;
  const members = [...crew.members].sort((a, b) =>
    roleRank[a.role] - roleRank[b.role]
    || Number(b.user.status !== 'offline') - Number(a.user.status !== 'offline')
    || a.user.displayName.localeCompare(b.user.displayName));

  return (
    <>
      {/* ---- the banner, full width ---- */}
      <div className="relative h-[200px] flex-none">
        <CrewBanner appearance={crew.appearance} className="h-full w-full" />
        <span aria-hidden className="absolute inset-0 bg-[linear-gradient(to_top,var(--bg-0)_4%,color-mix(in_srgb,var(--bg-0)_55%,transparent)_45%,transparent_80%)]" />
        <div className="absolute inset-x-7 bottom-4 flex items-end gap-4">
          <CrewMark tag={crew.tag} appearance={crew.appearance} size={88} className="flex-none rounded-[calc(20px*var(--radius-mul,1))] shadow-[0_14px_30px_-12px_rgba(0,0,0,.8)] ring-4 ring-[var(--bg-0)]" />
          <div className="min-w-0 flex-1 pb-1">
            <h2 className="m-0 truncate text-[28px] leading-tight font-extrabold tracking-[-.03em] text-ink">{crew.name}</h2>
            <p className="m-0 mt-1 flex flex-wrap items-center gap-x-2 text-[14px] text-dim">
              <span className="font-mono font-bold" style={{ color: colour }}>{crew.tag}</span>
              <span aria-hidden>·</span>
              <span>{crew.memberCount} {crew.memberCount === 1 ? 'member' : 'members'}</span>
              {crew.region ? <><span aria-hidden>·</span><span>{crew.region}</span></> : null}
              {crew.language ? <><span aria-hidden>·</span><span>{crew.language}</span></> : null}
            </p>
          </div>
        </div>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 z-[2] grid h-10 w-10 place-items-center rounded-full bg-[rgba(0,0,0,.45)] text-white/90 backdrop-blur-sm transition-colors hover:bg-[rgba(0,0,0,.65)] hover:text-white"
          >
            <Icon name="close" size={17} stroke={2.2} />
          </button>
        ) : null}
        {crew.rank ? (
          <span className="absolute top-4 left-5 flex items-center gap-1.5 rounded-full bg-[rgba(0,0,0,.5)] px-3 py-1 text-[13px] font-bold text-white backdrop-blur-sm">
            <Icon name="trophy" size={14} />
            Rank #{crew.rank}
          </span>
        ) : null}
      </div>

      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        {/* ---- who they are ---- */}
        <div className="min-h-0 flex-1 overflow-y-auto px-7 pt-1 pb-6">
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {!mine ? (
              <Button
                kind="primary"
                disabled={busy || crew.settings.join === 'invite'}
                onClick={() => void act('/api/crews/' + crew.id + '/join', {}, crew.settings.join === 'open' ? 'Joined' : 'Asked to join')}
                className="h-11 px-5 text-[15px]"
              >
                {joinLabel}
              </Button>
            ) : (
              <span className="flex h-11 items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-[color-mix(in_srgb,var(--good)_14%,transparent)] px-4 text-[14.5px] font-bold text-good">
                <Icon name="checkCirc" size={17} />
                {mine === 'owner' ? 'You own this crew' : mine === 'officer' ? 'You are an officer' : 'You are a member'}
              </span>
            )}
            {canManage ? (
              <Button kind="outline" onClick={() => setSettings(true)} className="h-11 px-4">
                <Icon name="sliders" size={15} />
                Crew settings
              </Button>
            ) : null}
          </div>

          <div className="pugo-panel mt-6 rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)]">
            <CrewLevelBar level={crew.level} appearance={crew.appearance} />
          </div>

          <div className="mt-3 grid grid-cols-3 gap-3">
            <Stat value={crew.level.level} label="Crew level" colour={colour} />
            <Stat value={crew.score.levels} label="Member levels" />
            <Stat value={crew.score.xp.toLocaleString()} label="XP together" />
          </div>

          <div className="mt-6">
            <h3 className="m-0 mb-2 text-[15px] font-bold text-ink">About</h3>
            <p className={cn('m-0 text-[14.5px] leading-relaxed', crew.description ? 'text-dim' : 'text-ghost')}>
              {crew.description || 'This crew has not written anything about itself yet.'}
            </p>
            {crew.tags?.length ? (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {crew.tags.map((t) => <span key={t} className="rounded-full bg-surface-2 px-3 py-1 text-[13px] font-semibold text-dim">{t}</span>)}
              </div>
            ) : null}
          </div>

          {canManage && crew.requests.length ? (
            <div className="mt-6">
              <h3 className="m-0 mb-2 text-[15px] font-bold text-ink">Wants to join <span className="text-mute">{crew.requests.length}</span></h3>
              <div className="flex flex-col gap-1.5">
                {crew.requests.map((r) => (
                  <div key={r.user.id} className="flex items-center gap-3 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-1 px-3 py-2 shadow-[inset_0_0_0_1px_var(--line-soft)]">
                    <Avatar user={r.user} size={32} />
                    <button type="button" onClick={(e) => openUserCard(r.user, e.currentTarget)} className="min-w-0 flex-1 truncate text-left text-[14px] hover:underline"><UserName user={r.user} /></button>
                    <Button size="sm" kind="primary" disabled={busy} onClick={() => void act('/api/crews/' + crew.id + '/requests/' + r.user.id, { accept: true }, 'Added')}>Accept</Button>
                    <Button size="sm" kind="ghost" disabled={busy} onClick={() => void act('/api/crews/' + crew.id + '/requests/' + r.user.id, { accept: false })}>Decline</Button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {reporting ? (
            <div className="pugo-panel mt-6 rounded-[calc(16px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)]">
              <h3 className="m-0 mb-2 text-[15px] font-bold text-ink">Why are you reporting this crew?</h3>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="What is wrong with it?"
                className="w-full resize-none rounded-[var(--radius)] border border-line-soft bg-surface-inset p-2.5 text-[14px] outline-none focus:border-accent"
              />
              <div className="mt-2 flex justify-end gap-2">
                <Button size="sm" kind="ghost" onClick={() => setReporting(false)}>Cancel</Button>
                <Button size="sm" kind="primary" disabled={busy || !reason.trim()}
                  onClick={async () => {
                    await act('/api/crews/' + crew.id + '/report', { reason }, 'Reported. Staff will look.');
                    setReporting(false); setReason('');
                  }}>
                  Send report
                </Button>
              </div>
            </div>
          ) : null}

          <div className="mt-6 flex items-center gap-3 border-t border-line-soft pt-4">
            {mine ? (
              <Button kind="ghost" size="sm" disabled={busy}
                onClick={async () => {
                  if (await confirm('Leave ' + crew.name + '?', 'You would have to be let back in.', { confirmLabel: 'Leave', danger: true })) {
                    await act('/api/crews/' + crew.id + '/leave', {}, 'Left the crew');
                    onClose?.();
                  }
                }}>
                Leave crew
              </Button>
            ) : null}
            <span className="flex-1" />
            {!reporting ? (
              <button type="button" onClick={() => setReporting(true)} className="text-[13px] font-semibold text-mute transition-colors hover:text-bad">Report crew</button>
            ) : null}
          </div>
        </div>

        {/* ---- the people ---- */}
        <aside aria-label="Members" className="flex min-h-0 flex-none flex-col border-t border-line-soft bg-surface-1 md:w-[340px] md:border-t-0 md:border-l">
          <div className="flex items-center gap-2 px-5 pt-5 pb-3">
            <h3 className="m-0 flex-1 text-[15px] font-bold text-ink">Members</h3>
            <span className="rounded-full bg-surface-2 px-2.5 py-0.5 text-[12.5px] font-bold text-dim tabular-nums">{crew.memberCount}</span>
          </div>
          {crew.membersHidden ? (
            <p className="m-0 px-5 pb-5 text-[13.5px] text-mute">This crew keeps its member list to itself.</p>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">
              {members.map((m) => (
                <div key={m.user.id} className="group flex items-center gap-3 rounded-[calc(12px*var(--radius-mul,1))] px-2 py-2 transition-colors hover:bg-surface-2">
                  <button
                    type="button"
                    onClick={(e) => openUserCard(m.user, e.currentTarget)}
                    aria-label={'Show profile card of ' + m.user.displayName}
                    className="flex min-w-0 flex-1 items-center gap-3 rounded-[10px] text-left outline-none focus-visible:shadow-[0_0_0_2px_var(--accent)]"
                  >
                    <Avatar user={m.user} size={36} presence />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] font-semibold"><UserName user={m.user} profile={false} /></span>
                      <span className="block text-[12px] text-mute">{m.role === 'owner' ? 'Owner' : m.role === 'officer' ? 'Officer' : 'Member'}</span>
                    </span>
                  </button>
                  {m.role === 'owner' ? <Icon name="crown" size={15} className="flex-none text-warn" /> : null}
                  {mine === 'owner' && m.role !== 'owner' && m.user.id !== me?.id ? (
                    <button
                      type="button"
                      disabled={busy}
                      title={m.role === 'officer' ? 'Make a member again' : 'Make an officer'}
                      onClick={() => void act('/api/crews/' + crew.id + '/members/' + m.user.id, { role: m.role === 'officer' ? 'member' : 'officer' }, m.role === 'officer' ? 'Now a member' : 'Now an officer')}
                      className="grid h-7 w-7 flex-none place-items-center rounded-[8px] text-ghost opacity-0 transition-[opacity,color] group-hover:opacity-100 hover:text-accent"
                    >
                      <Icon name="crown" size={13} />
                    </button>
                  ) : null}
                  {canManage && m.role !== 'owner' && m.user.id !== me?.id ? (
                    <button
                      type="button"
                      disabled={busy}
                      title="Remove from crew"
                      onClick={() => void act('/api/crews/' + crew.id + '/members/' + m.user.id, { remove: true }, 'Removed')}
                      className="grid h-7 w-7 flex-none place-items-center rounded-[8px] text-ghost opacity-0 transition-[opacity,color] group-hover:opacity-100 hover:text-bad"
                    >
                      <Icon name="close" size={13} />
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </aside>
      </div>

      {settings ? (
        <CrewWorkspace crew={crew} onClose={() => setSettings(false)} onChanged={refresh} />
      ) : null}
      {dialog}
    </>
  );
}

function Stat({ value, label, colour }: { value: number | string; label: string; colour?: string }) {
  return (
    <div className="pugo-panel rounded-[calc(16px*var(--radius-mul,1))] bg-surface-1 px-3 py-3.5 text-center shadow-[inset_0_0_0_1px_var(--line-soft)]">
      <div className="text-[22px] leading-none font-extrabold tabular-nums" style={colour ? { color: colour } : undefined}>
        {value}
      </div>
      <div className="mt-1.5 text-[12px] font-semibold text-mute">{label}</div>
    </div>
  );
}

